import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import classNames from 'classnames';
import { useClickAway, useToggle } from 'react-use';
import { nanoid } from 'nanoid';
import { twMerge } from 'tailwind-merge';
export const Select = forwardRef(({ name, label, options, open = false, disabled, id, allowNotStrictFilter = false, helper, noOptionsText = 'No results', errors, onChange, value, labelDirection = 'col', labelClasses, className, placeholder, onInputChange, size, preIcon, postIcon, isSuccess, readOnly, dataTestId, initialInput, onFocus, optionsAbove = false, scrollToOption }, ref) => {
    const optionsRef = useRef(null);
    const listRef = useRef(null);
    const [focus, toggleFocus] = useToggle(false);
    const [isOptionSelected, toggleIsOptionSelected] = useToggle(false);
    const [filter, setFilter] = useState(initialInput ?? '');
    const isPostIcon = errors || isSuccess || postIcon;
    const handleChangeInput = (event) => {
        onInputChange && onInputChange(event.target.value);
        toggleFocus(true);
        setFilter(event.target.value);
    };
    const handleClickOption = (item) => {
        onChange(item);
        toggleFocus(false);
        setFilter(onInputChange && value?.value !== item.value
            ? initialInput && initialInput === value?.value
                ? initialInput
                : item.label
            : '');
        toggleIsOptionSelected(true);
    };
    const handleClickInput = () => {
        toggleFocus();
        !onInputChange && setFilter('');
    };
    useClickAway(optionsRef, () => {
        toggleFocus(false);
        !onInputChange && setFilter('');
    });
    useEffect(() => {
        initialInput && setFilter(initialInput);
    }, [initialInput]);
    const wrapperClassName = twMerge(classNames(`relative flex flex-${labelDirection} items-baseline w-full`, {
        'z-10': focus,
        'gap-1 md:gap-2': !size,
        'gap-1': size === 'sm',
        'gap-2': size === 'md' || size === 'lg'
    }), className);
    const responsiveTextClassName = classNames({
        'text-mBase md:text-base': !size,
        'text-sm': size === 'sm',
        'text-base': size === 'md',
        'text-mBase': size === 'lg'
    });
    const labelClassName = twMerge(classNames('min-w-[120px] font-semibold', disabled ? 'text-gray' : 'text-gray-700', responsiveTextClassName), labelClasses);
    const helperWrapperClassName = 'w-full';
    const inputWrapperClassName = 'relative w-full';
    const valueClassName = classNames('absolute left-0 pointer-events-none truncate w-[inherit]', disabled ? 'text-gray' : ' text-gray-700', {
        'top-[13px] text-mBase md:top-[9px] md:text-base': !size,
        'top-[7.5px] text-sm': size === 'sm',
        'top-[9px] text-base': size === 'md',
        'top-[13px] text-mBase': size === 'lg'
    }, isPostIcon
        ? {
            'pr-12 md:pr-14': !size,
            'pr-12': size === 'sm',
            'pr-14': size === 'md',
            'pr-16': size === 'lg'
        }
        : {
            'pr-10 md:pr-9': !size,
            'pr-8': size === 'sm',
            'pr-9': size === 'md',
            'pr-10': size === 'lg'
        }, preIcon
        ? {
            'pl-10 md:pl-9': !size,
            'pl-8': size === 'sm',
            'pl-9': size === 'md',
            'pl-10': size === 'lg'
        }
        : 'pl-3');
    const iconClassName = classNames('absolute inset-y-0 my-auto pointer-events-none', {
        'w-6 h-6 md:w-5 md:h-5': !size,
        'w-4 h-4': size === 'sm',
        'w-5 h-5': size === 'md',
        'w-6 h-6': size === 'lg'
    });
    const preIconClassName = classNames(iconClassName, 'left-3 text-gray');
    const postIconClassName = classNames(iconClassName, 'right-3', errors ? 'text-red' : isSuccess ? 'text-green' : 'text-gray');
    const arrowIconClassName = classNames(iconClassName, 'text-gray', isPostIcon
        ? {
            'right-8 md:right-9': !size,
            'right-8': size === 'sm',
            'right-9': size === 'md',
            'right-10': size === 'lg'
        }
        : 'right-3');
    const inputClassName = classNames('w-full outline-none ring-1 rounded-md text-gray-700 font-medium', 'placeholder:text-gray disabled:ring-gray-200 disabled:bg-gray-200 focus:shadow-none focus:ring-2 focus:ring-blue', {
        'bg-green-50': isSuccess
    }, {
        'h-[50px] py-[13px] text-mBase md:h-[38px] md:py-[9px] md:text-base': !size,
        'h-[32px] py-[7.5px] text-sm': size === 'sm',
        'h-[38px] py-[9px] text-base': size === 'md',
        'h-[50px] py-[13px] text-mBase': size === 'lg'
    }, isPostIcon
        ? {
            'pr-12 md:pr-14': !size,
            'pr-12': size === 'sm',
            'pr-14': size === 'md',
            'pr-16': size === 'lg'
        }
        : {
            'pr-10 md:pr-9': !size,
            'pr-8': size === 'sm',
            'pr-9': size === 'md',
            'pr-10': size === 'lg'
        }, preIcon
        ? {
            'pl-10 md:pl-9': !size,
            'pl-8': size === 'sm',
            'pl-9': size === 'md',
            'pl-10': size === 'lg'
        }
        : 'pl-3', !errors ? 'ring-gray-400 shadow-sm' : 'ring-2 ring-red shadow-none');
    const optionsClassName = classNames('absolute w-full mt-1 max-h-[300px] bg-white shadow-lg rounded-md py-2 overflow-auto', optionsAbove ? 'mb-1 bottom-full' : 'mt-1 top-full');
    const noOptionsClassName = classNames('flex gap-2 justify-center px-3 py-2 text-gray font-semibold', responsiveTextClassName);
    const getOptionClassName = (selected = false, disabled = false) => classNames('flex gap-2 justify-between px-3 py-2 text-gray-700 group hover:bg-blue hover:text-white hover:fill-white', responsiveTextClassName, selected ? 'font-bold' : 'font-semibold', disabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer');
    const helperClassName = classNames('mt-2', {
        'text-base md:text-sm': !size,
        'text-xs': size === 'sm',
        'text-sm': size === 'md',
        'text-base': size === 'lg'
    }, !!errors ? 'text-red' : 'text-gray');
    const checkIconClassName = classNames('text-blue group-hover:text-white', {
        'w-6 h-6 md:w-5 h-5': !size,
        'w-4 h-4': size === 'sm',
        'w-5 h-5': size === 'md',
        'w-6 h-6': size === 'lg'
    });
    const filteredOptions = options.filter((item) => allowNotStrictFilter ? true : item.label.toLowerCase().includes(filter.toLowerCase()));
    useEffect(() => {
        if (focus && scrollToOption && listRef.current) {
            const selectedOption = Array.from(listRef.current.querySelectorAll('div')).find((div) => div.innerHTML.includes(scrollToOption));
            if (selectedOption) {
                selectedOption.scrollIntoView({ block: 'center' });
            }
        }
    }, [focus, scrollToOption]);
    return (_jsxs("div", { className: wrapperClassName, ref: optionsRef, children: [label && (_jsx("label", { className: labelClassName, htmlFor: id, children: label })), _jsxs("div", { className: helperWrapperClassName, children: [_jsxs("div", { className: inputWrapperClassName, children: [!onInputChange && value && !filter && (_jsx("span", { className: valueClassName, "data-testid": "selected_option", children: value.label })), preIcon && _jsx(Icon, { className: preIconClassName, name: preIcon }), _jsx("input", { autoComplete: "off", className: inputClassName, "data-testid": dataTestId, disabled: disabled, id: id, name: name, placeholder: value?.label ? '' : placeholder, readOnly: readOnly, ref: ref, type: "text", value: filter, onChange: handleChangeInput, onClick: handleClickInput, onFocus: onFocus }), isPostIcon && (_jsx(Icon, { className: postIconClassName, name: isSuccess ? 'check-circle' : errors ? 'error' : postIcon })), _jsx(Icon, { className: arrowIconClassName, name: "arrow-down" }), ((focus && options.length > 0) || (open && !isOptionSelected)) && (_jsx("div", { className: optionsClassName, "data-testid": "all_options_in_dropdown", ref: listRef, children: filteredOptions.length > 0 ? (filteredOptions.map((item) => (_jsxs("div", { className: getOptionClassName(item.label === value?.label, item.disabled), "data-testid": "option", ...(!item.disabled && { onClick: () => handleClickOption(item) }), children: [item.label, item.value === value?.value && (_jsx(Icon, { className: checkIconClassName, name: "check" }))] }, nanoid())))) : (_jsx("div", { className: noOptionsClassName, children: noOptionsText }, nanoid())) }))] }), helper && _jsx("div", { className: helperClassName, children: helper })] })] }));
});
